import React, { useEffect, useState } from "react";
import { Building2, Loader2, Plus, RefreshCw, Save, Trash2, X, Sparkles, ExternalLink } from "lucide-react";
import {
  adminRecruiters as adminCompanies, adminUpsertRecruiter as adminUpsertCompany,
  adminDeleteRecruiter as adminDeleteCompany, adminRegenerateRecruiter as adminRegenerateCompany,
} from "@/lib/api";

/* Admin → Companies: the FACTS behind every prep hub. Edit eligibility, rounds,
   official URL. Saving invalidates that company's AI hub so it
   regenerates from the new facts on next view. */

const inputCls = "w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-700 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-[#C2410C]";
const EMPTY = { slug: "", name: "", aliases: "", category: "", pattern_id: "", official_url: "", last_verified: "", verify_note: "",
  eligibility: { min_pct_10: "", min_pct_12: "", min_cgpa: "", max_active_backlogs: "", max_gap_years: "", branches: "all", notes: "" },
  rounds: [{ name: "", note: "" }] };

export default function AdminCompanies() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => adminCompanies().then(({ data }) => setRows(data.companies || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const open = (c) => setEdit(c ? {
    ...c, aliases: (c.aliases || []).join(", "),
    eligibility: { min_pct_10: "", min_pct_12: "", min_cgpa: "", max_active_backlogs: "", max_gap_years: "", branches: "all", notes: "", ...Object.fromEntries(Object.entries(c.eligibility || {}).map(([k, v]) => [k, v === null ? "" : (Array.isArray(v) ? v.join(", ") : v)])) },
    rounds: c.rounds?.length ? c.rounds : EMPTY.rounds,
  } : { ...EMPTY, _new: true });

  const regen = async (slug) => {
    setBusy(slug); setMsg("");
    try { const { data } = await adminRegenerateCompany(slug); setMsg(`${slug}: hub regenerated (${data.sections.length} sections).`); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Regenerate failed."); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-companies">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-stone-100 flex items-center gap-2"><Building2 size={26} className="text-[#C2410C]" /> Companies</h1>
          <p className="text-sm text-stone-400 mt-1 max-w-2xl">The facts behind every student prep hub — eligibility, rounds, official source. The AI writes the hub around these facts and never adds numbers of its own.</p>
        </div>
        <button onClick={() => open(null)} data-testid="company-add" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412]"><Plus size={15} /> Add company</button>
      </div>
      {msg && <p className="mt-4 text-sm text-amber-300">{msg}</p>}

      <div className="mt-6 rounded-2xl border border-stone-800 bg-[#1c1917] overflow-hidden">
        {!rows ? <p className="px-6 py-8 text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm text-stone-300">
            <thead><tr className="border-b border-stone-800 text-[10px] font-mono uppercase tracking-widest text-stone-500">
              {["Company", "Eligibility (10th / 12th / CGPA / backlogs / gap)", "Pattern", "Hub", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((c) => { const e = c.eligibility || {}; return (
                <tr key={c.slug} className="border-b border-stone-800/60 hover:bg-stone-900/40">
                  <td className="px-4 py-3"><p className="text-stone-100 font-medium">{c.name}</p><p className="text-xs text-stone-500">{c.category} · <span className="font-mono">{c.slug}</span>{c.last_verified === "seeded" && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 text-[10px]">seeded — verify</span>}</p></td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-400">{[e.min_pct_10, e.min_pct_12, e.min_cgpa, e.max_active_backlogs, e.max_gap_years].map((v) => v ?? "—").join(" / ")}</td>
                  <td className="px-4 py-3 text-xs">{c.pattern_id ? <span className="text-emerald-400 font-mono">{c.pattern_id}</span> : <span className="text-stone-600">none</span>}</td>
                  <td className="px-4 py-3 text-xs text-stone-500">{c.hub_generated_at ? new Date(c.hub_generated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "not yet"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => regen(c.slug)} disabled={busy === c.slug} title="Regenerate AI hub" className="p-2 rounded-lg text-stone-400 hover:text-amber-300">{busy === c.slug ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}</button>
                    <button onClick={() => open(c)} className="ml-1 px-3 py-1.5 rounded-full bg-stone-100 text-stone-900 text-xs font-medium hover:bg-white">Edit</button>
                  </td>
                </tr>); })}
            </tbody>
          </table></div>
        )}
      </div>

      {edit && <Editor c={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function Editor({ c, onClose, onSaved }) {
  const [f, setF] = useState(c);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setE = (k) => (e) => setF((x) => ({ ...x, eligibility: { ...x.eligibility, [k]: e.target.value } }));
  const setRow = (key, i, k) => (e) => setF((x) => ({ ...x, [key]: x[key].map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)) }));
  const addRow = (key, blank) => setF((x) => ({ ...x, [key]: [...x[key], blank] }));
  const delRow = (key, i) => setF((x) => ({ ...x, [key]: x[key].filter((_, j) => j !== i) }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async () => {
    const slug = (f.slug || f.name || "").toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
    if (!f.name.trim() || !slug) { setErr("Name is required."); return; }
    setSaving(true); setErr("");
    const el = f.eligibility;
    try {
      await adminUpsertCompany(slug, {
        name: f.name.trim(), aliases: f.aliases.split(",").map((s) => s.trim()).filter(Boolean), category: f.category || null,
        pattern_id: f.pattern_id || null, official_url: f.official_url || null, last_verified: f.last_verified || "admin",
        verify_note: f.verify_note || null,
        eligibility: { min_pct_10: num(el.min_pct_10), min_pct_12: num(el.min_pct_12), min_cgpa: num(el.min_cgpa), max_active_backlogs: num(el.max_active_backlogs),
          max_gap_years: num(el.max_gap_years), branches: !el.branches || el.branches === "all" ? "all" : el.branches.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean), notes: el.notes || null },
        rounds: f.rounds.filter((r) => r.name),
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };
  const del = async () => { if (!window.confirm(`Delete ${f.name}? Students lose its hub.`)) return; await adminDeleteCompany(f.slug).catch(() => {}); onSaved(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full overflow-y-auto bg-[#1c1917] border-l border-stone-700 p-6" onClick={(e) => e.stopPropagation()} data-testid="company-editor">
        <div className="flex items-center justify-between"><h2 className="font-display text-2xl text-stone-100">{c._new ? "Add company" : `Edit ${c.name}`}</h2>
          <div className="flex gap-2">{!c._new && <button onClick={del} className="p-2 text-stone-500 hover:text-red-400"><Trash2 size={15} /></button>}<button onClick={onClose} className="p-2 text-stone-500 hover:text-stone-200"><X size={18} /></button></div></div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <input placeholder="Name *" value={f.name} onChange={set("name")} className={inputCls} data-testid="company-f-name" />
          <input placeholder="Slug (auto from name)" value={f.slug} onChange={set("slug")} disabled={!c._new} className={`${inputCls} font-mono disabled:opacity-50`} />
          <input placeholder="Aliases, comma-separated (for drive matching)" value={f.aliases} onChange={set("aliases")} className={`${inputCls} col-span-2`} />
          <input placeholder="Category (IT services / Product / Consulting)" value={f.category || ""} onChange={set("category")} className={inputCls} />
          <input placeholder="Test pattern id (e.g. tcs_nqt) or blank" value={f.pattern_id || ""} onChange={set("pattern_id")} className={`${inputCls} font-mono`} />
          <input placeholder="Official careers URL" value={f.official_url || ""} onChange={set("official_url")} className={`${inputCls} col-span-2`} />
        </div>

        <p className="mt-6 text-xs font-mono uppercase tracking-widest text-[#C2410C]">Eligibility (leave blank = not published)</p>
        <div className="mt-2 grid grid-cols-3 gap-3">
          {[["min_pct_10", "Min 10th %"], ["min_pct_12", "Min 12th %"], ["min_cgpa", "Min CGPA"], ["max_active_backlogs", "Max active backlogs"], ["max_gap_years", "Max gap (years)"]].map(([k, l]) => (
            <label key={k} className="text-xs text-stone-400">{l}<input type="number" step="any" value={f.eligibility[k]} onChange={setE(k)} className={`${inputCls} mt-1`} /></label>
          ))}
          <label className="text-xs text-stone-400">Branches ("all" or cse, it, ece)<input value={f.eligibility.branches} onChange={setE("branches")} className={`${inputCls} mt-1`} /></label>
        </div>
        <textarea rows={2} placeholder="Eligibility notes shown to students" value={f.eligibility.notes || ""} onChange={setE("notes")} className={`${inputCls} mt-3 resize-none`} />


        <p className="mt-6 text-xs font-mono uppercase tracking-widest text-[#C2410C]">Rounds (in order)</p>
        {f.rounds.map((r, i) => (
          <div key={i} className="mt-2 grid grid-cols-[1fr_2fr_32px] gap-2">
            <input placeholder="Round" value={r.name} onChange={setRow("rounds", i, "name")} className={inputCls} />
            <input placeholder="What happens" value={r.note || ""} onChange={setRow("rounds", i, "note")} className={inputCls} />
            <button onClick={() => delRow("rounds", i)} className="text-stone-500 hover:text-red-400"><X size={14} /></button>
          </div>
        ))}
        <button onClick={() => addRow("rounds", { name: "", note: "" })} className="mt-2 text-xs text-stone-400 hover:text-stone-200 inline-flex items-center gap-1"><Plus size={12} /> round</button>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <input placeholder="Last verified (e.g. 2026-09 · official page)" value={f.last_verified === "seeded" ? "" : (f.last_verified || "")} onChange={set("last_verified")} className={inputCls} />
          <input placeholder="Verify note shown to students" value={f.verify_note || ""} onChange={set("verify_note")} className={inputCls} />
        </div>
        {f.official_url && <a href={f.official_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-300"><ExternalLink size={12} /> open official page to verify</a>}
        {err && <p className="mt-3 text-sm text-red-400">{err}</p>}
        <button onClick={save} disabled={saving} data-testid="company-save" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save facts (hub regenerates on next view)
        </button>
        <p className="mt-2 text-[11px] text-stone-500 flex items-center gap-1"><RefreshCw size={10} /> Saving clears the cached AI hub for this company.</p>
      </div>
    </div>
  );
}
